import {
  InputText,
  TextV2,
  InputType,
  SvgLock2Bold,
  Chip,
  SvgHierarchy8Bold,
} from '@insights-ltd/design-library/components';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  styled,
} from '@mui/material';
import { TeamResponse, useGetPaginatedTeamsForOrganisation } from 'api';
import { DisplayItem } from 'components/DisplayItem';
import {
  UseFormSetValue,
  UseFormWatch,
  UseFormRegister,
  FieldError,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ListLayoutContext,
  PaginationContext,
} from 'components/VirtualisedAutocomplete/autoComplete.helper';
import PaginatedListBoxComponent from 'components/VirtualisedAutocomplete/PaginatedListBoxComponent';
import { useState } from 'react';
import { useDebounceValue } from 'components/hooks/useDebouncedValue';
import { TeamFormData } from '../AddToTeamForm';
const StyledLock = styled(SvgLock2Bold)(() => ({
  fill: 'currentcolor',
  transform: 'scale(67%)',
}));

function preventFormSubmission(event: React.KeyboardEvent<HTMLDivElement>) {
  if (event.key === 'Enter') event.preventDefault();
}
const StyledInput = styled(InputText)(() => ({
  '& > div': {
    height: '3rem',
    width: '100%',
  },
}));

const StyledHierarchy8Bold = styled(SvgHierarchy8Bold)(({ theme }) => ({
  width: '1rem',
  fill: theme.palette.blue.dark,
  marginRight: '.5rem',
  marginTop: '.1rem',
  transform: 'scale(120%)',
}));

const TEAM_ROW_HEIGHT = 48;
const TEAM_LIST_MAX_HEIGHT = 380;

export const ExistingTeamFields = ({
  organisationId,
  watch,
  setValue,
  register,
  error,
}: {
  setValue: UseFormSetValue<TeamFormData>;
  watch: UseFormWatch<TeamFormData>;
  organisationId: string;
  register: UseFormRegister<TeamFormData>;
  error?: FieldError;
}) => {
  const { t } = useTranslation();
  const { ref: selectTeamRef } = register('selectedTeam', {
    required: true,
  });
  const selectedTeamValue = watch('selectedTeam');
  const [selectedTeamData, setSelectedTeamData] = useState<TeamResponse | null>(
    null,
  );

  const [inputValue, setInputValue] = useState<string>('');
  const debouncedSearchTerm: string = useDebounceValue(inputValue);

  const {
    isFetching,
    isLoading,
    data,
    hasNextPage: hasMore,
    fetchNextPage: loadMore,
  } = useGetPaginatedTeamsForOrganisation(organisationId, debouncedSearchTerm);

  const {
    name,
    learnerCount: count,
    organisationContext,
    visibility,
  } = selectedTeamData || {};
  const isMultiOrg = organisationContext?.type === 'MULTIPLE';
  const isPrivate = visibility === 'PRIVATE';
  return selectedTeamValue ? (
    <DisplayItem
      name={
        <Box sx={{ alignContent: 'baseline', display: 'flex' }}>
          {isMultiOrg && (
            <StyledHierarchy8Bold
              aria-label={t(
                'ui.event-management.learners.teams.create.multi-org-option',
              )}
            />
          )}
          <TextV2 component="span" color="textPrimary" variant="bodyBold">
            {name}
          </TextV2>
        </Box>
      }
      labelKey={'ui.event-management.add-to-team.existing-team.label'}
      Action={{
        text: 'ui.event-management.generic.button.change',
        onClick: () => {
          setValue('selectedTeam', '');
          setSelectedTeamData(null);
          setInputValue('');
        },
        color: 'primary',
      }}
      tags={[
        isPrivate && (
          <Chip
            key="team-visibility-tag"
            color="grey"
            icon={<StyledLock />}
            label={t('ui.event-management.teams.permissions.label-private')}
            aria-label={t(
              'ui.event-management.teams.permissions.label-private',
            )}
            sx={{ marginLeft: '1rem' }}
          />
        ),
        <Box key={'learner-count'}>
          <TextV2
            component="span"
            sx={{
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              fontSize: '1rem !important',
              marginLeft: '1.625rem !important',
            }}
          >
            {t('ui.event-management.add-to-team.existing-team.team-info', {
              count,
            })}
          </TextV2>
        </Box>,
      ].filter(Boolean)}
    />
  ) : (
    <PaginationContext.Provider value={{ hasMore, isFetching, loadMore }}>
      <ListLayoutContext.Provider
        value={{ maxHeight: TEAM_LIST_MAX_HEIGHT, rowHeight: TEAM_ROW_HEIGHT }}
      >
        <Autocomplete
          options={data?.items || []}
          fullWidth
          id="add-to-team-autocomplete"
          data-testid="add-to-team-autocomplete"
          filterOptions={(options) => options}
          onChange={(_, changeValue) => {
            setValue('selectedTeam', changeValue?.id || '');
            setSelectedTeamData(changeValue || null);
          }}
          loadingText={t('ui.event-management.loading')}
          noOptionsText={t('ui.event-management.generic.no-options-text')}
          ref={selectTeamRef}
          getOptionLabel={(option) => option.name || ''}
          value={selectedTeamData}
          renderOption={(fullProps, val) => {
            const { color, ...rest } = fullProps;
            return (
              <li {...rest} key={val.id} data-testid={`add-to-team-${val.id}`}>
                <TextV2
                  component="span"
                  variant="bodyBold"
                  color="primary"
                  sx={{
                    display: 'block',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {val.name}
                </TextV2>
              </li>
            );
          }}
          inputValue={inputValue}
          onInputChange={(_, value) => setInputValue(value)}
          renderInput={(params: AutocompleteRenderInputParams) => {
            const { size, id, ...rest } = params;
            const { ref } = rest.slotProps.input;
            const { children, ...props } = rest.slotProps.htmlInput;
            return (
              <div ref={ref}>
                <StyledInput
                  {...props}
                  id={'team-select-input'}
                  label={t(
                    'ui.event-management.add-to-team.existing-team.label',
                  )}
                  placeholder={t(
                    'ui.event-management.add-to-team.existing-team.placeholder',
                  )}
                  required={true}
                  error={!!error}
                  data-testid={`team-select-input`}
                  type={'text' as InputType}
                  onKeyDown={preventFormSubmission}
                />
              </div>
            );
          }}
          slots={{ listbox: PaginatedListBoxComponent }}
          loading={isLoading}
        />
      </ListLayoutContext.Provider>
    </PaginationContext.Provider>
  );
};
